import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Select from "../../../components/BaseUI/Select";
import Checkbox from "../../../components/BaseUI/Checkbox";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import Pagination from "../../../components/BaseUI/Pagination";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const sourceLabel = (s) =>
  s === "purchased" ? "Satın alınan" : s === "mixed" ? "Karma" : "Üretilen";

const unitLabel = (u) => {
  if (!u) return "—";
  if (typeof u === "object") {
    const sym = u.symbol?.trim();
    const name = u.name?.trim();
    return sym || name || "—";
  }
  return "—";
};

const equivalentUnitSymbol = (u) => {
  if (!u || typeof u !== "object") return "";
  return String(u.symbol || u.name || "").trim();
};

const partUnitSymbol = (p) => {
  const u = p?.unitId;
  if (u && typeof u === "object") {
    return String(u.symbol || u.name || "").trim();
  }
  return unitLabel(p?.unitId) === "—" ? "" : unitLabel(p?.unitId);
};

const formatStoredEquivalentValue = (raw) => {
  if (raw == null || raw === "") return "";
  const n = Number(raw);
  return Number.isFinite(n)
    ? Number.isInteger(n)
      ? String(n)
      : String(n)
    : String(raw);
};

const partEquivalentFromDb = (p) => {
  if (!p || typeof p !== "object") {
    return { line: "", sortKey: "", status: "none", title: "" };
  }
  const primary = partUnitSymbol(p);
  const eqUnit = equivalentUnitSymbol(p.equivalentUnitId);
  const value = formatStoredEquivalentValue(p.equivalentValue);

  if (value && eqUnit && primary) {
    const line = `1 ${primary} = ${value} ${eqUnit}`;
    return {
      line,
      sortKey: line,
      status: "complete",
      title: `1 parça birimi (${primary}) = ${value} ${eqUnit}`,
    };
  }

  const partial = [primary, value, eqUnit].filter(Boolean);
  if (partial.length) {
    return {
      line: partial.join(" · "),
      sortKey: partial.join("-"),
      status: "partial",
      title: `Kayıtlı karşılık: ${partial.join(", ")}`,
    };
  }

  if (p.isRawMaterial) {
    return {
      line: "",
      sortKey: "\uffff",
      status: "incomplete",
      title: "Hammadde — karşılık eksik",
    };
  }

  return { line: "", sortKey: "", status: "none", title: "" };
};

const rawMaterialEquivalentLabel = (p) => partEquivalentFromDb(p).line;
const rawMaterialSortKey = (p) => partEquivalentFromDb(p).sortKey;

const sectionStyle = {
  border: "1px solid #e2e5e8",
  borderRadius: 8,
  padding: "12px 14px",
  background: "var(--m-card-background-color, #fff)",
};

const sectionTitleStyle = {
  fontSize: "0.8rem",
  fontWeight: 700,
  color: "#334155",
  marginBottom: 10,
};

const sectionTitle = (step, title) => (
  <div style={sectionTitleStyle}>
    <span style={{ color: "#94a3b8", marginRight: 6 }}>{step}.</span>
    {title}
  </div>
);

const tableColumns = [
  {
    name: "Ad",
    dataType: "text",
    id: 1,
    customSorter: (a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || "")),
  },
  {
    name: "Kod",
    dataType: "text",
    id: 2,
    customSorter: (a, b) =>
      String(a?.code || "").localeCompare(String(b?.code || "")),
  },
  {
    name: "Birim",
    dataType: "text",
    id: 3,
    customSorter: (a, b) =>
      unitLabel(a?.unitId).localeCompare(unitLabel(b?.unitId)),
  },
  {
    name: "Varyant",
    id: 4,
    customSorter: (a, b) =>
      Number(a?.variantCount ?? 0) - Number(b?.variantCount ?? 0),
  },
  {
    name: "Kaynak",
    dataType: "text",
    id: 5,
    customSorter: (a, b) =>
      String(sourceLabel(a?.source)).localeCompare(
        String(sourceLabel(b?.source)),
      ),
  },
  {
    name: "Karşılık",
    dataType: "text",
    id: 9,
    customSorter: (a, b) =>
      rawMaterialSortKey(a).localeCompare(rawMaterialSortKey(b)),
  },
  { name: "Ürün modeli", dataType: "text", id: 8, customSorter: (a, b) =>
      String(a?.deviceModel?.code || "").localeCompare(
        String(b?.deviceModel?.code || ""),
      ),
  },
  {
    name: "Durum",
    dataType: "text",
    id: 6,
    customSorter: (a, b) => Number(!!b?.isActive) - Number(!!a?.isActive),
  },
  { name: "İşlemler", id: 7, sortable: false },
];

const variantDisplay = (v) =>
  v?.variant ?? v?.prefix ?? (typeof v === "string" ? v : "");

const emptyForm = () => ({
  name: "",
  code: "",
  unitId: "",
  source: "produced",
  isFinalProduct: false,
  deviceModel: "",
  isActive: true,
  isRawMaterial: false,
  equivalentUnitId: "",
  equivalentValue: "",
  selectedVariantIds: [],
});

const Parts = () => {
  useTitle("Rovion MES - Parçalar");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/parts",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [parts, setParts] = useState([]);
  const [units, setUnits] = useState([]);
  const [models, setModels] = useState([]);
  const [partVariants, setPartVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });
  const [quickVariantModal, setQuickVariantModal] = useState(false);
  const [quickVariantData, setQuickVariantData] = useState({ variant: "", notes: "" });
  const [quickVariantSaving, setQuickVariantSaving] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalItems, setTotalItems] = useState(0);

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("parts-list", tableColumns);

  const dismissToast = useCallback(
    (id) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((prev) => [...prev, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchParts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/parts`, {
        ...authHeaders(),
        params: { page: currentPage, limit: itemsPerPage, search: searchQuery },
      });
      setParts(data.parts || []);
      setTotalItems(data.total || 0);
    } catch {
      addToast("Parçalar yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast, currentPage, itemsPerPage, searchQuery]);

  const fetchUnits = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/part-units`,
        authHeaders(),
      );
      setUnits(data.units || []);
    } catch {}
  }, [authHeaders]);

  const fetchModels = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/device-models`,
        authHeaders(),
      );
      setModels(Array.isArray(data) ? data : data.models || []);
    } catch {}
  }, [authHeaders]);

  const fetchPartVariants = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/part-variants`,
        authHeaders(),
      );
      setPartVariants(data.variants || []);
    } catch {}
  }, [authHeaders]);

  useEffect(() => {
    fetchParts();
    fetchUnits();
    fetchModels();
    fetchPartVariants();
  }, [fetchParts, fetchUnits, fetchModels, fetchPartVariants]);

  const openCreate = () => {
    setFormData(emptyForm());
    setEditingItem(null);
    setSidebarOpened(true);
  };

  const openEdit = async (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || "",
      code: item.code || "",
      unitId: String(item.unitId?._id || item.unitId || ""),
      source: item.source || "produced",
      isFinalProduct: !!item.isFinalProduct,
      deviceModel: String(item.deviceModel?._id || item.deviceModel || ""),
      isActive: item.isActive !== false,
      isRawMaterial: !!item.isRawMaterial,
      equivalentUnitId: String(
        item.equivalentUnitId?._id || item.equivalentUnitId || "",
      ),
      equivalentValue:
        item.equivalentValue != null && item.equivalentValue !== ""
          ? String(item.equivalentValue)
          : "",
      selectedVariantIds: [],
    });
    setSidebarOpened(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/parts/${item._id}`,
        authHeaders(),
      );
      const variantIds = (data.variants || [])
        .map((v) => String(v.variantId?._id || v.variantId || ""))
        .filter((id) => id && id !== "undefined");
      setFormData((s) => ({ ...s, selectedVariantIds: variantIds }));
    } catch {
      addToast("Varyantlar yüklenemedi", "warning");
    }
  };

  const activeUnits = units.filter((u) => u.isActive !== false);
  const unitOptions = [
    { value: "", label: "— Birim seçilmedi —" },
    ...activeUnits.map((u) => ({
      value: String(u._id),
      label: u.symbol ? `${u.symbol} — ${u.name}` : u.name,
    })),
  ];

  const primaryUnit = activeUnits.find(
    (u) => String(u._id) === String(formData.unitId),
  );
  const equivalentUnitOptions = [
    { value: "", label: "— Karşılık birimi —" },
    ...activeUnits
      .filter((u) => String(u._id) !== String(formData.unitId))
      .map((u) => ({
        value: String(u._id),
        label: u.symbol ? `${u.symbol} — ${u.name}` : u.name,
      })),
  ];
  const selectedEquivalentUnitSymbol =
    activeUnits.find((u) => String(u._id) === String(formData.equivalentUnitId))
      ?.symbol || "";

  const activePartVariants = partVariants.filter((v) => v.isActive !== false);
  const variantOptions = activePartVariants.map((v) => {
    const variant = variantDisplay(v);
    const notes = v.notes || v.label;
    return {
      value: String(v._id),
      label: notes ? `${variant} — ${notes}` : variant,
    };
  });
  const selectedVariantIdStrings = (formData.selectedVariantIds || []).map(
    String,
  );
  const selectedVariantsForPreview = activePartVariants.filter((v) =>
    selectedVariantIdStrings.includes(String(v._id)),
  );

  const closeSidebar = () => {
    setSidebarOpened(false);
    setEditingItem(null);
    setFormData(emptyForm());
  };

  const buildPartPayload = () => {
    const payload = {
      name: formData.name.trim(),
      code: formData.code.trim(),
      source: formData.source,
      isActive: formData.isActive,
      isFinalProduct:
        formData.source !== "purchased" && !!formData.isFinalProduct,
      unitId: formData.unitId || null,
      isRawMaterial: !!formData.isRawMaterial,
    };
    if (
      formData.source !== "purchased" &&
      formData.isFinalProduct &&
      formData.deviceModel
    ) {
      payload.deviceModel = formData.deviceModel;
    }
    if (formData.isRawMaterial) {
      payload.equivalentUnitId = formData.equivalentUnitId || null;
      const eqVal = parseFloat(
        String(formData.equivalentValue || "").replace(",", "."),
      );
      payload.equivalentValue = Number.isFinite(eqVal) ? eqVal : null;
    }
    return payload;
  };

  const handleSave = async () => {
    if (saving) return;
    if (!formData.name.trim()) {
      addToast("Ad zorunludur", "danger");
      return;
    }
    if (!formData.code.trim()) {
      addToast("Kod zorunludur", "danger");
      return;
    }
    if (formData.isRawMaterial) {
      if (!formData.unitId) {
        addToast("Hammadde için parça birimi seçin", "danger");
        return;
      }
      if (!formData.equivalentUnitId) {
        addToast("Karşılık birimi seçin", "danger");
        return;
      }
      const eqVal = parseFloat(
        String(formData.equivalentValue || "").replace(",", "."),
      );
      if (!Number.isFinite(eqVal) || eqVal <= 0) {
        addToast("Geçerli bir karşılık değeri girin", "danger");
        return;
      }
    }
    const variantIdsForSave = [
      ...new Set(
        [
          ...(formData.selectedVariantIds || []).map(String),
          ...selectedVariantsForPreview.map((v) => String(v._id)),
        ].filter((id) => id && id !== "undefined"),
      ),
    ];
    const useBulk = !editingItem && variantIdsForSave.length > 0;
    setSaving(true);
    try {
      if (editingItem) {
        const { data } = await axios.put(
          `${API_URL}/api/parts/${editingItem._id}`,
          { ...buildPartPayload(), variantIds: variantIdsForSave },
          authHeaders(),
        );
        const sync = data.variantSync;
        if (sync?.added || sync?.removed) {
          const parts = [];
          if (sync.added) parts.push(`${sync.added} eklendi`);
          if (sync.removed) parts.push(`${sync.removed} kaldırıldı`);
          if (sync.inactivated)
            parts.push(`${sync.inactivated} pasifleştirildi`);
          addToast(
            sync.skipped?.length
              ? `Güncellendi (${parts.join(", ")}), ${sync.skipped.length} atlandı`
              : `Güncellendi (${parts.join(", ")})`,
            sync.skipped?.length ? "warning" : "success",
          );
        } else if (sync?.skipped?.length) {
          addToast(
            sync.skipped[0]?.message || "Bazı varyantlar güncellenemedi",
            "warning",
          );
        } else {
          addToast("Güncellendi", "success");
        }
      } else if (useBulk) {
        const { data } = await axios.post(
          `${API_URL}/api/parts/bulk`,
          {
            ...buildPartPayload(),
            variantIds: variantIdsForSave,
          },
          authHeaders(),
        );
        const variantN = Math.max(0, (data.created || []).length - 1);
        const skipped = (data.skipped || []).length;
        if (variantN > 0 || data.mainPart) {
          addToast(
            skipped > 0
              ? `Ana parça + ${variantN} varyant oluşturuldu, ${skipped} atlandı`
              : `Ana parça + ${variantN} varyant oluşturuldu`,
            skipped > 0 ? "warning" : "success",
          );
        } else {
          addToast(data.message || "Parça oluşturulamadı", "danger");
        }
      } else {
        const singlePayload = { ...buildPartPayload() };
        const onlyVariantId = (formData.selectedVariantIds || []).map(
          String,
        )[0];
        if (onlyVariantId) singlePayload.variantId = onlyVariantId;
        await axios.post(`${API_URL}/api/parts`, singlePayload, authHeaders());
        addToast("Oluşturuldu", "success");
      }
      closeSidebar();
      fetchParts();
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/parts/${id}`, authHeaders());
      addToast("Silindi", "success");
      fetchParts();
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  const handleQuickVariantSave = async () => {
    if (!quickVariantData.variant.trim()) {
      addToast("Varyant zorunludur", "danger");
      return;
    }
    setQuickVariantSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/part-variants`,
        {
          variant: quickVariantData.variant.trim(),
          notes: quickVariantData.notes.trim() || undefined,
          isActive: true,
        },
        authHeaders()
      );
      addToast("Varyant oluşturuldu", "success");
      setQuickVariantModal(false);
      setQuickVariantData({ variant: "", notes: "" });
      await fetchPartVariants();
      if (data && data.variant) {
        setFormData((s) => ({
          ...s,
          selectedVariantIds: [...new Set([...(s.selectedVariantIds || []), String(data.variant._id)])],
        }));
      }
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setQuickVariantSaving(false);
    }
  };

  return (
    <AppPage
      title="Parçalar"
      actions={
        canWrite && (
          <Button
            appearance="primary-animated"
            onClick={openCreate}
            icon={<FiPlus />}
          >
            Yeni Parça
          </Button>
        )
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "error" ? "danger" : t.type || "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={quickVariantModal}
        onClose={() => setQuickVariantModal(false)}
        width="small"
      >
        <ModalHeader>Yeni Varyant Ekle</ModalHeader>
        <ModalBody>
          <div className="d-flex flex-column gap-3">
            <div>
              <Label required>Varyant</Label>
              <Input
                value={quickVariantData.variant}
                onChange={(v) => setQuickVariantData((s) => ({ ...s, variant: v }))}
                placeholder="örn. EU- veya A-"
              />
            </div>
            <div>
              <Label>Notlar</Label>
              <Input
                value={quickVariantData.notes}
                onChange={(v) => setQuickVariantData((s) => ({ ...s, notes: v }))}
                placeholder="örn. Avrupa pazarı vb."
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="primary-animated"
              onClick={handleQuickVariantSave}
              disabled={quickVariantSaving}
            >
              {quickVariantSaving ? "Kaydediliyor..." : "Oluştur"}
            </Button>
            <Button
              appearance="subtle"
              onClick={() => setQuickVariantModal(false)}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, id: null })}
        width="small"
      >
        <ModalHeader>Parçayı Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">
            Bu parçayı silmek istediğinizden emin misiniz?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="danger"
              onClick={() => handleDelete(confirmModal.id)}
            >
              Sil
            </Button>
            <Button
              appearance="subtle"
              onClick={() => setConfirmModal({ isOpen: false, id: null })}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <CardContainer className="d-flex flex-column h-100">
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <Table
          columns={tableColumns}
          visibleColumns={visibleColumns}
          loading={loading}
          hideFooter={true}
          search={true}
          externalSearch={true}
          searchValue={searchQuery}
          onSearch={(val) => {
            setSearchQuery(val);
            setCurrentPage(1);
          }}
          viewOptions={
            <ListViewOptionsPanel
              columns={tableColumns}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumnVisibility}
              onResetColumns={resetToDefault}
              showApply={false}
              showReset={false}
            />
          }
        >
          {parts.map((p) => (
            <Tr key={p._id} data={p}>
              {visibleColumns.includes(1) && (
              <Td>
                <strong style={{ fontSize: "0.875rem" }}>{p.name}</strong>
              </Td>
              )}
              {visibleColumns.includes(2) && (
              <Td>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                    background: "var(--m-badge-default-subtle-bg, #f1f5f9)",
                    padding: "2px 6px",
                    borderRadius: 4,
                  }}
                >
                  {p.code}
                </span>
              </Td>
              )}
              {visibleColumns.includes(3) && (
              <Td>
                <span className="text-muted small">{unitLabel(p.unitId)}</span>
              </Td>
              )}
              {visibleColumns.includes(4) && (
              <Td>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: "0.78rem",
                    color: Number(p.variantCount) > 0 ? "#475569" : "#94a3b8",
                  }}
                >
                  {Number(p.variantCount) > 0 ? p.variantCount : "—"}
                </span>
              </Td>
              )}
              {visibleColumns.includes(5) && (
              <Td>
                <span className="text-muted small">
                  {sourceLabel(p.source)}
                </span>
              </Td>
              )}
              {visibleColumns.includes(9) && (
              <Td>
                {(() => {
                  const info = partEquivalentFromDb(p);
                  if (info.status === "none") {
                    return <span className="text-muted small">—</span>;
                  }
                  return (
                    <span
                      className="d-inline-flex align-items-center gap-1"
                      style={{ maxWidth: 160, minWidth: 0 }}
                      title={info.title}
                    >
                      {info.line ? (
                        <span
                          className="text-truncate d-inline-block"
                          style={{
                            fontFamily: "monospace",
                            fontSize: "0.7rem",
                            color:
                              info.status === "complete"
                                ? "#b45309"
                                : "#64748b",
                            lineHeight: 1.2,
                            maxWidth: p.isRawMaterial ? 120 : 140,
                            verticalAlign: "middle",
                          }}
                        >
                          {info.line}
                        </span>
                      ) : (
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.7rem" }}
                        >
                          …
                        </span>
                      )}
                    </span>
                  );
                })()}
              </Td>
              )}
              {visibleColumns.includes(8) && (
              <Td>
                {p.isFinalProduct && p.deviceModel?.code ? (
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: "0.78rem",
                      color: "#0f766e",
                    }}
                  >
                    {p.deviceModel.code}
                  </span>
                ) : (
                  <span className="text-muted small">—</span>
                )}
              </Td>
              )}
              {visibleColumns.includes(6) && (
              <Td>
                {p.isActive ? (
                  <Lozenge appearance="success-subtle" isBold>
                    Aktif
                  </Lozenge>
                ) : (
                  <Lozenge appearance="default-subtle" isBold>
                    Pasif
                  </Lozenge>
                )}
              </Td>
              )}
              {visibleColumns.includes(7) && (
              <Td>
                <div className="d-flex gap-2">
                  {canWrite && (
                    <>
                      <IconButton
                        icon={<FiEdit2 />}
                        label="Düzenle"
                        appearance="subtle"
                        shape="circle"
                        onClick={() => openEdit(p)}
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        label="Sil"
                        appearance="subtle"
                        shape="circle"
                        onClick={() =>
                          setConfirmModal({ isOpen: true, id: p._id })
                        }
                      />
                    </>
                  )}
                </div>
              </Td>
              )}
            </Tr>
          ))}
        </Table>
        </div>
        <div style={{ marginTop: "1rem", flexShrink: 0 }}>
          <Pagination
            totalItems={totalItems}
            totalPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={({ pagination }) => {
              setCurrentPage(pagination.currentPage);
              setItemsPerPage(pagination.itemsPerPage);
            }}
          />
        </div>
      </CardContainer>

      <SidebarContainer
        show={sidebarOpened}
        title={editingItem ? "Parça Düzenle" : "Yeni Parça"}
        onClose={closeSidebar}
      >
        <div className="d-flex flex-column gap-3" style={{ paddingBottom: 16 }}>
          <div style={sectionStyle}>
            {sectionTitle(1, "Tanım")}
            <div className="d-flex flex-column gap-2">
              <div>
                <Label required>
                  {(formData.selectedVariantIds || []).length
                    ? "Temel ad"
                    : "Ad"}
                </Label>
                <Input
                  value={formData.name}
                  onChange={(v) => setFormData((s) => ({ ...s, name: v }))}
                  placeholder="örn. Vida M5"
                />
              </div>
              <div>
                <Label required>
                  {(formData.selectedVariantIds || []).length
                    ? "Temel kod"
                    : "Kod"}
                </Label>
                <Input
                  value={formData.code}
                  onChange={(v) => setFormData((s) => ({ ...s, code: v }))}
                  placeholder="örn. VD-M5-001"
                />
              </div>
              <div>
                <Select
                  className="mb-0"
                  label="Birim"
                  unique
                  options={unitOptions}
                  selected={formData.unitId ? [String(formData.unitId)] : [""]}
                  onSelected={(values) => {
                    const v = values?.[0]?.value || "";
                    setFormData((s) => ({
                      ...s,
                      unitId: v,
                      equivalentUnitId:
                        String(s.equivalentUnitId) === String(v)
                          ? ""
                          : s.equivalentUnitId,
                    }));
                  }}
                  placeholder={
                    unitOptions.length > 1
                      ? "Birim seçin"
                      : "Önce Parça Birimleri sayfasından birim ekleyin"
                  }
                  disabled={unitOptions.length <= 1}
                />
              </div>
              <div>
                <Checkbox
                  label="Hammadde"
                  isChecked={!!formData.isRawMaterial}
                  onChange={({ checked }) =>
                    setFormData((s) => ({
                      ...s,
                      isRawMaterial: checked,
                      equivalentUnitId: checked ? s.equivalentUnitId : "",
                      equivalentValue: checked ? s.equivalentValue : "",
                    }))
                  }
                />
              </div>
              {formData.isRawMaterial && (
                <div>
                  <div className="d-flex align-items-end gap-2">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Select
                        className="mb-0"
                        label="Karşılık birimi"
                        unique
                        options={equivalentUnitOptions}
                        selected={
                          formData.equivalentUnitId
                            ? [String(formData.equivalentUnitId)]
                            : [""]
                        }
                        onSelected={(values) => {
                          const v = values?.[0]?.value || "";
                          setFormData((s) => ({ ...s, equivalentUnitId: v }));
                        }}
                        placeholder={
                          equivalentUnitOptions.length > 1
                            ? "Karşılık birimi seçin"
                            : "Önce farklı bir parça birimi ekleyin"
                        }
                        disabled={equivalentUnitOptions.length <= 1}
                      />
                    </div>
                    <div style={{ flex: "0 0 120px" }}>
                      <Label required>Karşılık değeri</Label>
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        value={formData.equivalentValue}
                        onChange={(v) =>
                          setFormData((s) => ({ ...s, equivalentValue: v }))
                        }
                        placeholder="örn. 2.5"
                      />
                    </div>
                  </div>
                  {primaryUnit?.symbol && selectedEquivalentUnitSymbol ? (
                    <p
                      className="text-muted small mb-0 mt-1"
                      style={{ fontSize: "0.75rem" }}
                    >
                      1 {primaryUnit.symbol} = {formData.equivalentValue || "?"}{" "}
                      {selectedEquivalentUnitSymbol}
                    </p>
                  ) : null}
                </div>
              )}
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2 mt-1">
                  <Label className="mb-0" required={false}>
                    {editingItem ? "Varyantlar" : "Varyant"}
                  </Label>
                  <Button
                    appearance="primary-subtle"
                    size="sm"
                    onClick={() => {
                      setQuickVariantData({ variant: "", notes: "" });
                      setQuickVariantModal(true);
                    }}
                  >
                    Varyant Ekle
                  </Button>
                </div>
                <Select
                  className="mb-0"
                  label=""
                  unique={false}
                  options={variantOptions}
                  selected={selectedVariantIdStrings}
                  onSelected={(values) =>
                    setFormData((s) => ({
                      ...s,
                      selectedVariantIds: (values || []).map((o) =>
                        String(o.value),
                      ),
                    }))
                  }
                  placeholder={
                    variantOptions.length
                      ? editingItem
                        ? "Varyant ekle veya kaldır"
                        : "Varyant seçin"
                      : "Önce Parça Varyantları sayfasından varyant ekleyin"
                  }
                  disabled={variantOptions.length === 0}
                />
              </div>
            </div>
          </div>

          <div style={sectionStyle}>
            {sectionTitle(2, "Özellikler")}
            <div className="d-flex flex-column gap-2">
              <div>
                <Select
                  className="mb-0"
                  label="Kaynak"
                  unique
                  options={[
                    { value: "produced", label: "Üretilen" },
                    { value: "purchased", label: "Satın alınan" },
                    { value: "mixed", label: "Karma" },
                  ]}
                  selected={[formData.source || "produced"]}
                  onSelected={(values) => {
                    const v = values?.[0]?.value || "produced";
                    setFormData((s) => ({
                      ...s,
                      source: v,
                      isFinalProduct:
                        v === "purchased" ? false : s.isFinalProduct,
                      deviceModel: v === "purchased" ? "" : s.deviceModel,
                    }));
                  }}
                  placeholder="Kaynak seçin"
                />
              </div>
              {formData.source !== "purchased" && (
                <div>
                  <Checkbox
                    label="Nihai ürün"
                    isChecked={!!formData.isFinalProduct}
                    onChange={({ checked }) =>
                      setFormData((s) => ({
                        ...s,
                        isFinalProduct: checked,
                        deviceModel: checked ? s.deviceModel : "",
                      }))
                    }
                  />
                </div>
              )}
              {formData.source !== "purchased" && formData.isFinalProduct && (
                <div>
                  <Select
                    className="mb-0"
                    label="Bağlı cihaz modeli"
                    unique
                    options={[
                      { value: "", label: "— Bağlı değil —" },
                      ...models.map((m) => ({
                        value: String(m._id),
                        label: `${m.code} — ${m.name}`,
                      })),
                    ]}
                    selected={
                      formData.deviceModel
                        ? [String(formData.deviceModel)]
                        : [""]
                    }
                    onSelected={(values) => {
                      const v = values?.[0]?.value || "";
                      setFormData((s) => ({ ...s, deviceModel: v }));
                    }}
                    placeholder="Model seçin"
                  />
                </div>
              )}
              <div>
                <Select
                  className="mb-0"
                  label="Durum"
                  unique
                  options={[
                    { value: "1", label: "Aktif" },
                    { value: "0", label: "Pasif" },
                  ]}
                  selected={[formData.isActive ? "1" : "0"]}
                  onSelected={(values) => {
                    const v = values?.[0]?.value === "0" ? false : true;
                    setFormData((s) => ({ ...s, isActive: v }));
                  }}
                  placeholder="Durum seçin"
                />
              </div>
            </div>
          </div>

          <div className="pt-1 pb-1">
            <Button
              appearance="primary-animated"
              onClick={handleSave}
              disabled={saving}
              icon={editingItem ? <FiEdit2 /> : <FiPlus />}
              className="w-100"
            >
              {saving
                ? "Kaydediliyor..."
                : editingItem
                  ? "Güncelle"
                  : (formData.selectedVariantIds || []).length > 0
                    ? `${(formData.selectedVariantIds || []).length} parça oluştur`
                    : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>
    </AppPage>
  );
};

export default withAuth(Parts);
